import kbFlytt from "@/assets/kb-flytt.webp";
import kbPapper from "@/assets/kb-papper.webp";
import kbVardag from "@/assets/kb-vardag.webp";
import kbTrygghet from "@/assets/kb-trygghet.webp";

import { pathFor, type Locale } from "@/lib/i18n";

/** Ett låst kunskapsmaterial. Teaser visas öppet, brödtexten hämtas från servern. */
export type ResourceMeta = {
  /** Svensk slug (används som id i API:et) */
  slug: string;
  slugEn: string;
  image: string;
  minutes: number;
  title: string;
  titleEn: string;
  titleEs: string;
  intro: string;
  introEn: string;
  introEs: string;
  /** Innehållsförteckning som visas innan man låser upp */
  outline: string[];
  outlineEn: string[];
};

export const resources: ResourceMeta[] = [
  {
    slug: "flyttchecklistan",
    slugEn: "relocation-checklist",
    image: kbFlytt,
    minutes: 8,
    title: "Flyttchecklistan: 12 månader till inflyttning",
    titleEn: "The relocation checklist: 12 months to move-in",
    titleEs: "La lista de la mudanza: 12 meses hasta la llegada",
    intro:
      "En månad-för-månad-plan för familjer som flyttar till Costa del Sol – från första researchen till första skoldagen. Vad som behöver göras i Sverige, vad som görs på plats och i vilken ordning.",
    introEn:
      "A month-by-month plan for families moving to the Costa del Sol – from the first research to the first day of school. What to do in Sweden, what to do locally, and in which order.",
    introEs:
      "Un plan mes a mes para las familias que se mudan a la Costa del Sol, desde la primera búsqueda hasta el primer día de colegio: qué resolver en el país de origen, qué hacer aquí y en qué orden.",
    outline: [
      "Tidslinje 12–0 månader före flytten",
      "Vad du avslutar och behåller i Sverige",
      "Utflyttningsanmälan till Skatteverket – och när den inte ska göras",
      "Bostad: hyra först eller köpa direkt?",
      "Flyttfirma, bil och husdjur",
      "Första två veckorna på plats – i rätt ordning",
      "Tre missuppfattningar som kostar tid (tre månader, padrón, bilen)",
      "Bilen och husdjuren – vad som måste vara klart före avresan",
      "Kostnader första året som brukar överraska",
    ],
    outlineEn: [
      "Timeline from 12 months to moving day",
      "What to close and what to keep in Sweden",
      "Deregistering with the Swedish Tax Agency – and when not to",
      "Housing: rent first or buy straight away?",
      "Removals, cars and pets",
      "Your first two weeks in Spain – in the right order",
      "Three misconceptions that cost time (three months, padrón, the car)",
      "The car and the pets – what must be settled before you leave",
      "First-year costs that tend to surprise",
    ],
  },
  {
    slug: "myndigheter-och-papper",
    slugEn: "paperwork-and-authorities",
    image: kbPapper,
    minutes: 9,
    title: "Myndigheter och papper: NIE, TIE, padrón och skola",
    titleEn: "Paperwork and authorities: NIE, TIE, padrón and school",
    titleEs: "Trámites y documentación: NIE, TIE, padrón y colegio",
    intro:
      "Steg för steg genom de spanska ärenden som familjer möter första året – vilket papper som kräver vilket annat papper, var du bokar tid och vad du bör ta med dig.",
    introEn:
      "A step-by-step walk-through of the Spanish admin families meet in their first year – which document requires which, where to book an appointment and what to bring.",
    introEs:
      "Paso a paso por los trámites españoles del primer año: qué documento exige cuál, dónde se pide cita y qué hay que llevar. Todo empieza por el NIE.",
    outline: [
      "Ordningen mellan NIE, certificado de registro och TIE",
      "Cita previa: så bokar du tid – och vad du gör när det är fullt",
      "Empadronamiento (padrón) och varför den styr så mycket",
      "Sjukvård: EU-kort, S1, offentligt vårdcentralsnummer och privat försäkring",
      "Handlingar från Sverige: personbevis, betyg, apostille och auktoriserad översättning",
      "Digitalt certifikat och Cl@ve – nyckeln till spanska e-tjänster",
      "Dokumentmapp: vad varje familjemedlem bör ha kopior på",
      "Tremånadersregeln – och varför den inte är sex månader",
      "Tre sorters ”bosatt”: registrering, padrón och skattehemvist (183 dagar)",
      "Vad ni tar med till varje besök – och när ni bör anlita en gestor",
    ],
    outlineEn: [
      "The order between NIE, certificado de registro and TIE",
      "Cita previa: booking an appointment – and what to do when there are none",
      "Empadronamiento (padrón) and why it unlocks so much",
      "Healthcare: EHIC, S1, public health centre registration and private cover",
      "Documents from Sweden: certificates, school records, apostille and sworn translation",
      "Digital certificate and Cl@ve – the key to Spanish e-services",
      "Document folder: what every family member should have copies of",
      "The three-month rule – and why it is not six months",
      "Three kinds of “resident”: registration, padrón and tax residence (183 days)",
      "What to bring to each appointment – and when to use a gestor",
    ],
  },
  {
    slug: "vardagen-i-spanien",
    slugEn: "everyday-life-in-spain",
    image: kbVardag,
    minutes: 8,
    title: "Vardagen i Spanien: bostad, bank, bil och rutiner",
    titleEn: "Everyday life in Spain: housing, banking, cars and routines",
    titleEs: "La vida diaria en España: vivienda, banco, coche y rutinas",
    intro:
      "Det praktiska som ingen berättar i förväg: hyreskontrakt och avgifter, hur bankärenden fungerar, körkort och bil, öppettider, helgdagar och hur en vanlig familjevecka faktiskt ser ut.",
    introEn:
      "The practical things nobody tells you in advance: rental contracts and running costs, banking, driving licences and cars, opening hours, public holidays and what a normal family week actually looks like.",
    introEs:
      "Lo práctico que nadie cuenta de antemano: contratos y gastos, cómo funciona el banco, carné y coche, horarios, festivos y cómo es realmente una semana en familia.",
    outline: [
      "Hyreskontrakt: fianza, aval och vad kontraktet bör innehålla",
      "Löpande kostnader: comunidad, IBI, basura, el och vatten",
      "Bank, betalningar och autogiro (domiciliación)",
      "Körkort, bil, ITV och försäkring",
      "El, internet och mobilabonnemang",
      "Dagsrytm, öppettider och helgdagar i Andalusien",
      "Budgetposter som brukar överraska",
      "Ta med bilen: ITV, registreringsskatt efter CO2, befrielse och frister",
      "Att köra på utländska skyltar – riskerna och hur man rättar till det",
      "Körkort och DGT för EU och icke-EU",
    ],
    outlineEn: [
      "Rental contracts: deposit, guarantees and what the contract should cover",
      "Running costs: community fees, IBI, refuse charge, electricity and water",
      "Banking, payments and direct debits (domiciliación)",
      "Driving licences, cars, ITV and insurance",
      "Electricity, internet and mobile plans",
      "Daily rhythm, opening hours and public holidays in Andalusia",
      "Budget items that tend to surprise people",
      "Bringing the car: ITV, CO2-based registration tax, exemption and deadlines",
      "Driving on foreign plates – the risks and how to put it right",
      "Driving licences and the DGT for EU and non-EU holders",
    ],
  },
  {
    slug: "trygghet-och-viktiga-nummer",
    slugEn: "safety-and-key-numbers",
    image: kbTrygghet,
    minutes: 6,
    title: "Trygghet och viktiga nummer i Spanien",
    titleEn: "Safety and key numbers in Spain",
    titleEs: "Seguridad y números importantes en España",
    intro:
      "Samlade nummer och kontakter att spara i telefonen innan ni behöver dem: larm, sjukvård, polis, apotek, ambassad och konsulat – plus vad man säger och gör i de vanligaste situationerna.",
    introEn:
      "The numbers and contacts to save in your phone before you need them: emergency services, healthcare, police, pharmacies, embassy and consulate – plus what to say and do in the most common situations.",
    introEs:
      "Los números y contactos que conviene guardar antes de necesitarlos: emergencias, sanidad, policía, farmacias, embajada y consulados, además de qué decir y hacer en las situaciones más frecuentes.",
    outline: [
      "Larmnummer och vad du säger på spanska",
      "Sjukvård: vårdcentral, akuten, jourapotek och Salud Responde",
      "Polis: 091, 092, 062 – vem gör vad och hur du gör en denuncia",
      "Svenska ambassaden och konsulat i Spanien",
      "Barn, sol, vatten och trafik – de vanligaste riskerna",
      "Familjens beredskapskort att skriva ut",
      "Vid trafikolycka: 112, parte amistoso och rätt polis",
      "Om barnet blir sjukt – vårdcentral, urgencias eller jourapotek",
      "Bedrägerier mot nyinflyttade och vad en denuncia krävs till",
    ],
    outlineEn: [
      "Emergency numbers and what to say in Spanish",
      "Healthcare: health centre, A&E, duty pharmacies and Salud Responde",
      "Police: 091, 092, 062 – who does what and how to file a denuncia",
      "The Swedish embassy and consulates in Spain",
      "Children, sun, water and traffic – the most common risks",
      "A printable emergency card for the family",
      "In a road accident: 112, parte amistoso and the right police force",
      "If your child falls ill – health centre, urgencias or duty pharmacy",
      "Scams aimed at newcomers and what a denuncia is needed for",
    ],
  },
];

export function resourceBySlug(slug: string): ResourceMeta | undefined {
  return resources.find((resource) => {
    const spanishSlug = pathFor(`/kunskapsbank/${resource.slug}`, "es").split("/").filter(Boolean).at(-1);
    return resource.slug === slug || resource.slugEn === slug || spanishSlug === slug;
  });
}

export function resourceSlug(resource: ResourceMeta, locale: Locale): string {
  if (locale === "sv") return resource.slug;
  if (locale === "en") return resource.slugEn;
  return pathFor(`/kunskapsbank/${resource.slug}`, "es").split("/").filter(Boolean).at(-1) ?? resource.slugEn;
}

export function resourcePath(resource: ResourceMeta, locale: Locale): string {
  return pathFor(`/kunskapsbank/${resource.slug}`, locale);
}

export function resourceTitle(resource: ResourceMeta, locale: Locale): string {
  if (locale === "sv") return resource.title;
  if (locale === "es") return resource.titleEs;
  return resource.titleEn;
}

export function resourceIntro(resource: ResourceMeta, locale: Locale): string {
  if (locale === "sv") return resource.intro;
  if (locale === "es") return resource.introEs;
  return resource.introEn;
}

export function resourceOutline(resource: ResourceMeta, locale: Locale): string[] {
  return locale === "sv" ? resource.outline : resource.outlineEn;
}

/** Innehållsblock som renderas i det upplåsta materialet. */
export type ResourceBlock =
  | { type: "h2"; text: string }
  | { type: "p"; text: string }
  | { type: "list"; items: string[] }
  | { type: "steps"; items: { title: string; text: string }[] }
  | { type: "contacts"; items: { name: string; value: string; note?: string }[] }
  | { type: "note"; text: string };
